import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, Star, Clock, Monitor, Tag, Award, Shield } from "lucide-react";

const C = {
  bg: "#FAFAF8", white: "#FFFFFF", navy: "#1B2A4A", navyLight: "rgba(27,42,74,0.07)",
  gold: "#C9A452", goldLight: "rgba(201,164,82,0.12)", goldBorder: "rgba(201,164,82,0.3)",
  text: "#111827", muted: "#6B7280", light: "#9CA3AF",
  border: "#E8E3D8", borderLight: "#F0EDE6", section: "#F5F2EC",
};

const PHOTOS = {
  hero: "https://images.unsplash.com/photo-1683791736760-48d59a7eff9f?w=1400&q=80",
  woman: "https://images.unsplash.com/photo-1612709060421-596380268eaf?w=900&q=80",
  womanDriver: "https://images.unsplash.com/photo-1698840031871-c2021d5f2adc?w=900&q=80",
  car: "https://images.unsplash.com/photo-1683791736704-54eeae23f7d5?w=900&q=80",
};

const STATS = [
  { n: "12 ans", label: "d'expérience terrain" },
  { n: "98%", label: "taux de réussite" },
  { n: "500+", label: "stagiaires formés" },
  { n: "3", label: "formations disponibles" },
];

const FORMATIONS = [
  { tag: "VTC", title: "Formation VTC", prixBarre: "1 490 €", prix: "699 €", highlight: false },
  { tag: "Passerelle", title: "VTC → Taxi", prixBarre: "990 €", prix: "499 €", highlight: true },
  { tag: "Taxi", title: "Formation Taxi", prixBarre: "1 690 €", prix: "799 €", highlight: false },
];

// Dates CMA IDF 2026 (sessions officielles connues)
const CMA_DATES = [
  { session: "Session Printemps", examen: "29 mai 2026", cloture: "18 avril 2026", statut: "Clôturé" },
  { session: "Session Été", examen: "30 juin 2026", cloture: "22 mai 2026", statut: "Ouvert" },
  { session: "Session Automne", examen: "19 novembre 2026", cloture: "8 octobre 2026", statut: "À venir" },
  { session: "Session Hiver", examen: "28 janvier 2027", cloture: "17 décembre 2026", statut: "À venir" },
];

const AVANTAGES = [
  "Formateurs experts Taxi & VTC",
  "Formation professionnelle sérieuse et structurée",
  "Cours du soir en distanciel accompagné",
  "Coaching personnalisé jusqu'à la réussite de l'examen CMA",
  "Coaching personnalisé pour réussir l'examen CMA",
  "Paiement sécurisé en ligne (Stripe · CB)",
  "Paiement sécurisé en ligne (Stripe)",
  "Réponse sous 24h — WhatsApp : 07 49 12 42 82",
];

const AVIS = [
  { nom: "Karim D.", texte: "Formation sérieuse, coaching au top. Licence taxi obtenue du premier coup !", note: 5 },
  { nom: "Sophie M.", texte: "Passerelle VTC→Taxi en 2 semaines. Mme Ioudarene est une vraie professionnelle. Merci !", note: 5 },
  { nom: "Amina B.", texte: "En tant que maman, les cours du soir en distanciel m'ont permis de me former sans contrainte. Parfait !", note: 5 },
];

function GoldDivider() {
  return <div style={{ width: 56, height: 3, background: `linear-gradient(90deg, ${C.gold}, transparent)`, borderRadius: 2, margin: "16px 0" }} />;
}

function StatusBadge({ s }: { s: string }) {
  const map: Record<string, { bg: string; color: string }> = {
    "Ouvert": { bg: "rgba(22,163,74,0.1)", color: "#15803d" },
    "Clôturé": { bg: "rgba(107,114,128,0.1)", color: C.muted },
    "À venir": { bg: C.goldLight, color: "#92700a" },
  };
  const style = map[s] || map["À venir"];
  return (
    <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: style.bg, color: style.color }}>
      {s}
    </span>
  );
}

export default function Home() {
  return (
    <div style={{ background: C.bg }}>

      {/* ══ HERO ══ */}
      <section style={{ position: "relative", minHeight: "92vh", display: "flex", alignItems: "center", overflow: "hidden" }}>
        {/* Photo BG */}
        <div style={{ position: "absolute", inset: 0, backgroundImage: `url(${PHOTOS.hero})`, backgroundSize: "cover", backgroundPosition: "center right", filter: "brightness(0.25)" }} />
        <div style={{ position: "absolute", inset: 0, background: `linear-gradient(105deg, ${C.navy} 45%, rgba(27,42,74,0.6) 75%, transparent 100%)` }} />

        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "80px 24px", position: "relative", zIndex: 1 }}>
          <div style={{ maxWidth: 620 }}>
            {/* Badge */}
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 16px", borderRadius: 20, background: C.goldLight, border: `1px solid ${C.goldBorder}`, color: C.gold, fontSize: 12, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase" as const, marginBottom: 24 }}>
              <Award size={13} /> Formatrice experte depuis 12 ans · Bobigny, IDF
            </div>

            <h1 style={{ fontSize: "clamp(38px, 5.5vw, 64px)", fontWeight: 800, lineHeight: 1.1, color: "#fff", marginBottom: 20, fontFamily: "'Playfair Display', serif" }}>
              Devenez<br />
              <span style={{ color: C.gold }}>Chauffeur</span><br />
              Taxi ou VTC
            </h1>
            <GoldDivider />
            <p style={{ color: "rgba(255,255,255,0.8)", fontSize: 17, lineHeight: 1.8, marginBottom: 16, maxWidth: 520 }}>
              Formée et coachée par <strong style={{ color: "#fff" }}>Sonia</strong>, professionnelle du transport de personnes avec <strong style={{ color: C.gold }}>12 ans d'expérience</strong> sur le terrain.
            </p>
            <p style={{ color: "rgba(255,255,255,0.6)", fontSize: 14, lineHeight: 1.7, marginBottom: 32 }}>
              3 formations · Cours du soir en distanciel · Coaching inclus
            </p>

            {/* Promo */}
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "8px 16px", borderRadius: 8, background: C.goldLight, border: `1px solid ${C.goldBorder}`, color: C.gold, fontSize: 13, fontWeight: 700, marginBottom: 28 }}>
              <Tag size={13} /> Prix en promotion jusqu'au 30 septembre 2026
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: 14 }}>
              <Link to="/inscription" style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "15px 30px", borderRadius: 10, background: C.gold, color: C.navy, fontWeight: 800, fontSize: 15, textDecoration: "none", boxShadow: "0 8px 28px rgba(201,164,82,0.4)" }}>
                S'inscrire maintenant <ArrowRight size={18} />
              </Link>
              <a href={`https://wa.me/33749124282?text=Bonjour%20Sonia%2C%20je%20souhaite%20des%20infos%20sur%20les%20formations.`} target="_blank" rel="noreferrer"
                style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "15px 28px", borderRadius: 10, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.2)", color: "#fff", fontWeight: 600, fontSize: 15, textDecoration: "none" }}>
                💬 WhatsApp
              </a>
            </div>

            <div style={{ marginTop: 28, display: "flex", flexWrap: "wrap", gap: 16 }}>
              {["✓ Coaching inclus", "✓ Formateurs experts", "✓ Cours en distanciel"].map((t) => (
                <span key={t} style={{ fontSize: 13, color: "rgba(255,255,255,0.65)" }}>{t}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══ STATS ══ */}
      <section style={{ background: C.navy, padding: "0" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "36px 24px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 20 }}>
          {STATS.map((st) => (
            <div key={st.n} style={{ textAlign: "center" }}>
              <div style={{ fontSize: "clamp(22px, 3.5vw, 34px)", fontWeight: 800, color: C.gold, fontFamily: "'Playfair Display', serif" }}>{st.n}</div>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.55)", marginTop: 4, textTransform: "uppercase", letterSpacing: "0.08em" }}>{st.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ══ SECTION FEMMES ══ */}
      <section style={{ background: C.white, borderBottom: `1px solid ${C.border}` }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "80px 24px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 64, alignItems: "center" }}>
            {/* Photo */}
            <div style={{ position: "relative" }}>
              <div style={{ borderRadius: 20, overflow: "hidden", aspectRatio: "4/5", boxShadow: "0 24px 60px rgba(27,42,74,0.15)" }}>
                <img src={PHOTOS.woman} alt="Femme chauffeur professionnelle" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              </div>
              {/* Badge sur la photo */}
              <div style={{ position: "absolute", bottom: 24, left: -16, background: C.white, borderRadius: 14, padding: "14px 20px", boxShadow: "0 8px 30px rgba(27,42,74,0.14)", border: `1px solid ${C.border}` }}>
                <div style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 4 }}>Votre formatrice</div>
                <div style={{ fontWeight: 700, fontSize: 14, color: C.navy }}>Sonia</div>
                <div style={{ fontSize: 12, color: C.gold, fontWeight: 600 }}>12 ans d'expérience Taxi & VTC</div>
              </div>
            </div>

            {/* Texte */}
            <div>
              <div style={{ display: "inline-block", padding: "4px 14px", borderRadius: 20, background: C.goldLight, border: `1px solid ${C.goldBorder}`, color: "#92700a", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 16 }}>
                Expertise terrain
              </div>
              <h2 style={{ fontSize: "clamp(26px, 4vw, 40px)", fontWeight: 700, color: C.navy, marginBottom: 8, lineHeight: 1.2, fontFamily: "'Playfair Display', serif" }}>
                Une experte du secteur<br />à votre service
              </h2>
              <GoldDivider />
              <p style={{ color: C.muted, fontSize: 15, lineHeight: 1.8, marginBottom: 20 }}>
                Présidente et fondatrice de SKOOL FORMATION, <strong style={{ color: C.text }}>Sonia</strong> exerce dans le milieu du Taxi et VTC depuis <strong style={{ color: C.navy }}>12 ans</strong>. Elle connaît les réalités du terrain, les exigences de l'examen et les clés de la réussite professionnelle.
              </p>
              <p style={{ color: C.muted, fontSize: 15, lineHeight: 1.8, marginBottom: 28 }}>
                Son parcours lui permet de vous transmettre non seulement les connaissances théoriques indispensables, mais aussi l'expérience pratique qui fait la différence le jour de l'examen.
              </p>
              {[
                "12 ans d'expérience terrain Taxi & VTC",
                "Connaissance approfondie des examens CMA IDF",
                "Accompagnement humain et bienveillant",
                "Disponible via WhatsApp pour toutes vos questions",
              ].map((a) => (
                <div key={a} style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 10, fontSize: 14, color: C.text }}>
                  <CheckCircle size={16} color={C.gold} style={{ flexShrink: 0, marginTop: 2 }} />
                  {a}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══ SECTION FEMMES UNIQUEMENT ══ */}
      <section style={{ background: C.section, borderBottom: `1px solid ${C.border}` }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "80px 24px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 64, alignItems: "center" }}>
            {/* Texte */}
            <div>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "5px 14px", borderRadius: 20, background: "rgba(219,39,119,0.08)", border: "1px solid rgba(219,39,119,0.2)", color: "#be185d", fontSize: 11, fontWeight: 700, textTransform: "uppercase" as const, letterSpacing: "0.1em", marginBottom: 16 }}>
                ♀ Classes Femmes disponibles
              </div>
              <h2 style={{ fontSize: "clamp(24px, 4vw, 38px)", fontWeight: 700, color: C.navy, marginBottom: 8, lineHeight: 1.2, fontFamily: "'Playfair Display', serif" }}>
                Le transport de personnes,<br />
                <span style={{ color: C.gold }}>un secteur qui s'ouvre aux femmes</span>
              </h2>
              <GoldDivider />
              <p style={{ color: C.muted, fontSize: 15, lineHeight: 1.8, marginBottom: 20 }}>
                Le transport de personnes représente aujourd'hui une <strong style={{ color: C.text }}>opportunité professionnelle exceptionnelle pour les femmes</strong>. La demande de passagères souhaitant être conduites par une chauffeure est en forte croissance — un créneau à fort potentiel.
              </p>
              <p style={{ color: C.muted, fontSize: 15, lineHeight: 1.8, marginBottom: 28 }}>
                <strong style={{ color: C.text }}>Liberté des horaires, indépendance professionnelle, revenus significatifs</strong> — un métier idéal pour les mamans solos qui souhaitent conjuguer vie de famille et carrière épanouissante, sans contraintes d'horaires fixes.
              </p>

              {/* Avantages femmes */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 28 }}>
                {[
                  { icon: "💰", title: "Revenus élevés", desc: "Chiffre d'affaires à fort potentiel dès les premiers mois" },
                  { icon: "⏰", title: "Liberté totale", desc: "Vous choisissez vos horaires, idéal pour les mamans" },
                  { icon: "🚗", title: "Clientèle féminine", desc: "Forte demande de passagères pour des chauffeures femmes" },
                  { icon: "📈", title: "Indépendance", desc: "Statut autoentrepreneur ou salarié selon vos besoins" },
                ].map((item) => (
                  <div key={item.title} style={{ padding: "16px", borderRadius: 12, background: C.white, border: `1px solid ${C.border}` }}>
                    <div style={{ fontSize: 22, marginBottom: 8 }}>{item.icon}</div>
                    <div style={{ fontWeight: 700, fontSize: 13, color: C.navy, marginBottom: 4 }}>{item.title}</div>
                    <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>{item.desc}</div>
                  </div>
                ))}
              </div>

              <div style={{ padding: "16px 20px", borderRadius: 12, background: "rgba(219,39,119,0.05)", border: "1px solid rgba(219,39,119,0.15)", marginBottom: 24 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: "#be185d", marginBottom: 4 }}>♀ Classes exclusivement féminines</div>
                <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6 }}>
                  Sur demande, SKOOL FORMATION propose des sessions réservées aux femmes, dans un cadre bienveillant et solidaire, animées par Sonia elle-même.
                </div>
              </div>

              <a href={`https://wa.me/33749124282?text=Bonjour%20Sonia%2C%20je%20suis%20int%C3%A9ress%C3%A9e%20par%20une%20session%20f%C3%A9minine.`} target="_blank" rel="noreferrer"
                style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "13px 24px", borderRadius: 10, background: "#be185d", color: "#fff", fontWeight: 700, fontSize: 14, textDecoration: "none", boxShadow: "0 6px 20px rgba(190,24,93,0.25)" }}>
                💬 Demander une session féminine <ArrowRight size={15} />
              </a>
            </div>

            {/* Photo */}
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ borderRadius: 20, overflow: "hidden", aspectRatio: "3/4", boxShadow: "0 20px 50px rgba(27,42,74,0.12)" }}>
                <img src={PHOTOS.womanDriver} alt="Chauffeuse professionnelle" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              </div>
              <div style={{ padding: "20px 24px", borderRadius: 16, background: C.white, border: `1px solid ${C.border}`, textAlign: "center" }}>
                <div style={{ fontSize: 28, marginBottom: 6 }}>👩‍💼</div>
                <div style={{ fontWeight: 700, fontSize: 15, color: C.navy, marginBottom: 6 }}>Rejoignez le mouvement</div>
                <div style={{ fontSize: 13, color: C.muted }}>De plus en plus de femmes font le choix du transport de personnes pour sa flexibilité et ses revenus attractifs.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ FORMATIONS APERÇU ══ */}
      <section style={{ background: C.white, borderBottom: `1px solid ${C.border}` }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "80px 24px" }}>
          <div style={{ textAlign: "center", marginBottom: 52 }}>
            <div style={{ display: "inline-block", padding: "4px 14px", borderRadius: 20, background: C.navyLight, color: C.navy, fontSize: 11, fontWeight: 700, textTransform: "uppercase" as const, letterSpacing: "0.1em", marginBottom: 16 }}>3 formations disponibles</div>
            <h2 style={{ fontSize: "clamp(24px, 4vw, 38px)", fontWeight: 700, color: C.navy, marginBottom: 8, fontFamily: "'Playfair Display', serif" }}>Mobilité Professionnelle</h2>
            <p style={{ color: C.muted, fontSize: 15, maxWidth: 480, margin: "0 auto" }}>Cours du soir en distanciel · 7 modules · 2 semaines · Coaching inclus</p>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(290px, 1fr))", gap: 20 }}>
            {FORMATIONS.map((f) => (
              <div key={f.title} style={{ borderRadius: 16, padding: 28, background: f.highlight ? C.navy : C.white, border: `1px solid ${f.highlight ? C.navy : C.border}`, boxShadow: f.highlight ? "0 16px 40px rgba(27,42,74,0.18)" : "0 2px 12px rgba(27,42,74,0.06)" }}>
                <div style={{ display: "inline-block", padding: "3px 11px", borderRadius: 20, marginBottom: 14, fontSize: 11, fontWeight: 700, textTransform: "uppercase" as const, letterSpacing: "0.1em", background: f.highlight ? C.gold : C.navyLight, color: f.highlight ? C.navy : C.navy }}>{f.tag}</div>
                <h3 style={{ fontSize: 20, fontWeight: 700, color: f.highlight ? "#fff" : C.navy, marginBottom: 14, fontFamily: "'Playfair Display', serif" }}>{f.title}</h3>
                <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 16 }}>
                  <span style={{ fontSize: 13, color: f.highlight ? "rgba(255,255,255,0.4)" : C.light, textDecoration: "line-through" }}>{f.prixBarre}</span>
                  <span style={{ fontSize: 30, fontWeight: 800, color: f.highlight ? C.gold : C.navy, fontFamily: "'Playfair Display', serif" }}>{f.prix}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 16 }}>
                  <Tag size={11} color={f.highlight ? C.gold : "#92700a"} />
                  <span style={{ fontSize: 11, color: f.highlight ? C.gold : "#92700a", fontWeight: 600 }}>Promo jusqu'au 30 sept. 2026</span>
                </div>
                <div style={{ display: "flex", gap: 14, marginBottom: 20 }}>
                  <span style={{ fontSize: 11, color: f.highlight ? "rgba(255,255,255,0.5)" : C.light, display: "flex", alignItems: "center", gap: 4 }}><Clock size={11} /> 7 modules</span>
                  <span style={{ fontSize: 11, color: f.highlight ? "rgba(255,255,255,0.5)" : C.light, display: "flex", alignItems: "center", gap: 4 }}><Monitor size={11} /> Distanciel</span>
                </div>
                <Link to="/formations" style={{ color: f.highlight ? C.gold : C.navy, fontSize: 13, fontWeight: 700, textDecoration: "none" }}>
                  En savoir plus →
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ DATES CMA IDF ══ */}
      <section style={{ background: C.section, borderBottom: `1px solid ${C.border}` }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "80px 24px" }}>
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <div style={{ display: "inline-block", padding: "4px 14px", borderRadius: 20, background: C.goldLight, border: `1px solid ${C.goldBorder}`, color: "#92700a", fontSize: 11, fontWeight: 700, textTransform: "uppercase" as const, letterSpacing: "0.1em", marginBottom: 16 }}>Calendrier officiel</div>
            <h2 style={{ fontSize: "clamp(22px, 4vw, 34px)", fontWeight: 700, color: C.navy, marginBottom: 8, fontFamily: "'Playfair Display', serif" }}>Sessions Examen CMA Île-de-France</h2>
            <p style={{ color: C.muted, fontSize: 14, maxWidth: 480, margin: "0 auto" }}>Examens Taxi & VTC CMA Paris Île-de-France 2026–2027</p>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 16 }}>
            {CMA_DATES.map((d) => (
              <div key={d.session} style={{ borderRadius: 16, padding: "24px 20px", background: C.white, border: `1px solid ${d.statut === "Ouvert" ? C.gold : C.border}`, boxShadow: d.statut === "Ouvert" ? `0 4px 20px ${C.goldLight}` : "0 2px 8px rgba(27,42,74,0.04)", position: "relative" }}>
                {d.statut === "Ouvert" && (
                  <div style={{ position: "absolute", top: -10, left: "50%", transform: "translateX(-50%)", padding: "3px 12px", borderRadius: 20, background: C.gold, color: C.navy, fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em", whiteSpace: "nowrap" }}>
                    ⚡ Inscription ouverte
                  </div>
                )}
                <div style={{ marginBottom: 12 }}>
                  <StatusBadge s={d.statut} />
                </div>
                <div style={{ fontWeight: 700, fontSize: 13, color: C.navy, marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.06em" }}>{d.session}</div>
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 11, color: C.light, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 2 }}>Date d'examen</div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: d.statut === "Ouvert" ? C.navy : C.text }}>{d.examen}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: C.light, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 2 }}>Clôture inscriptions</div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: d.statut === "Ouvert" ? "#dc2626" : C.muted }}>{d.cloture}</div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 28, padding: "16px 24px", borderRadius: 12, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.18)", textAlign: "center" }}>
            <span style={{ color: "#dc2626", fontSize: 14, fontWeight: 700 }}>
              ⚠️ DERNIÈRE SESSION avant la pause estivale — Clôture des inscriptions : <strong>22 mai 2026</strong>
            </span>
          </div>

          <div style={{ textAlign: "center", marginTop: 24 }}>
            <a href={`https://wa.me/33749124282?text=Bonjour%2C%20je%20veux%20m'inscrire%20pour%20la%20session%20du%2029%20septembre%202026`} target="_blank" rel="noreferrer"
              style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "13px 26px", borderRadius: 10, background: C.navy, color: "#fff", fontWeight: 700, fontSize: 14, textDecoration: "none", boxShadow: "0 6px 20px rgba(27,42,74,0.2)" }}>
              💬 Réserver ma place pour le 29 sept. 2026 <ArrowRight size={15} />
            </a>
          </div>
        </div>
      </section>

      {/* ══ AVANTAGES ══ */}
      <section style={{ background: C.white, borderBottom: `1px solid ${C.border}` }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "80px 24px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 64, alignItems: "center" }}>
          <div>
            <div style={{ display: "inline-block", padding: "4px 14px", borderRadius: 20, background: C.navyLight, color: C.navy, fontSize: 11, fontWeight: 700, textTransform: "uppercase" as const, letterSpacing: "0.1em", marginBottom: 16 }}>Pourquoi nous choisir</div>
            <h2 style={{ fontSize: "clamp(24px, 4vw, 36px)", fontWeight: 700, color: C.navy, marginBottom: 8, lineHeight: 1.2, fontFamily: "'Playfair Display', serif" }}>Votre réussite est notre engagement</h2>
            <GoldDivider />
            <p style={{ color: C.muted, fontSize: 15, lineHeight: 1.8, marginBottom: 28 }}>
              SKOOL FORMATION ne se contente pas de vous former — nous vous coachons jusqu'à la réussite de votre examen CMA, avec un suivi personnalisé à chaque étape.
            </p>
            <Link to="/inscription" style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "13px 24px", borderRadius: 10, background: C.navy, color: "#fff", fontWeight: 700, fontSize: 14, textDecoration: "none", boxShadow: "0 6px 20px rgba(27,42,74,0.2)" }}>
              Démarrer ma formation <ArrowRight size={15} />
            </Link>
          </div>
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 14 }}>
            {AVANTAGES.map((a) => (
              <li key={a} style={{ display: "flex", alignItems: "flex-start", gap: 12, fontSize: 14, color: C.text, padding: "10px 14px", borderRadius: 10, background: C.section, border: `1px solid ${C.borderLight}` }}>
                <CheckCircle size={16} color={C.gold} style={{ flexShrink: 0, marginTop: 1 }} />
                {a}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ══ PHOTO CAR ══ */}
      <section style={{ height: 340, position: "relative", overflow: "hidden" }}>
        <img src={PHOTOS.car} alt="Véhicule professionnel VTC" style={{ width: "100%", height: "100%", objectFit: "cover", filter: "brightness(0.6)" }} />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to right, rgba(27,42,74,0.9) 0%, rgba(27,42,74,0.3) 60%, transparent 100%)", display: "flex", alignItems: "center" }}>
          <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 24px", width: "100%" }}>
            <div style={{ maxWidth: 480 }}>
              <div style={{ fontWeight: 700, fontSize: 11, color: C.gold, textTransform: "uppercase", letterSpacing: "0.14em", marginBottom: 12 }}>Notre engagement</div>
              <h3 style={{ fontSize: "clamp(20px, 3.5vw, 32px)", fontWeight: 700, color: "#fff", marginBottom: 14, fontFamily: "'Playfair Display', serif" }}>
                "Votre réussite est notre mission"
              </h3>
              <p style={{ color: "rgba(255,255,255,0.7)", fontSize: 14, lineHeight: 1.7 }}>
                Chaque stagiaire est accompagné individuellement par Sonia et son équipe jusqu'à la réussite de l'examen.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ══ AVIS ══ */}
      <section style={{ background: C.section, borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "72px 24px" }}>
          <div style={{ textAlign: "center", marginBottom: 44 }}>
            <h2 style={{ fontSize: "clamp(22px, 4vw, 34px)", fontWeight: 700, color: C.navy, fontFamily: "'Playfair Display', serif" }}>Avis de nos stagiaires</h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 20 }}>
            {AVIS.map((a) => (
              <div key={a.nom} style={{ borderRadius: 16, padding: 28, background: C.white, border: `1px solid ${C.border}`, boxShadow: "0 2px 12px rgba(27,42,74,0.05)" }}>
                <div style={{ display: "flex", gap: 3, marginBottom: 16 }}>
                  {Array.from({ length: a.note }).map((_, i) => <Star key={i} size={14} fill={C.gold} color={C.gold} />)}
                </div>
                <p style={{ color: C.muted, fontSize: 14, lineHeight: 1.7, marginBottom: 16, fontStyle: "italic" }}>"{a.texte}"</p>
                <div style={{ fontWeight: 700, fontSize: 13, color: C.navy }}>{a.nom}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ CTA ══ */}
      <section style={{ background: C.navy, padding: "80px 24px", textAlign: "center" }}>
        <div style={{ maxWidth: 580, margin: "0 auto" }}>
          <div style={{ fontWeight: 700, fontSize: 11, color: C.gold, textTransform: "uppercase", letterSpacing: "0.14em", marginBottom: 16 }}>Passez à l'action</div>
          <h2 style={{ fontSize: "clamp(24px, 4vw, 38px)", fontWeight: 700, color: "#fff", marginBottom: 12, fontFamily: "'Playfair Display', serif" }}>
            Prête à changer de vie ?
          </h2>
          <p style={{ color: "rgba(255,255,255,0.65)", fontSize: 16, marginBottom: 10, lineHeight: 1.7 }}>
            Rejoignez les stagiaires formés par SKOOL FORMATION et accédez à une carrière épanouissante dans le transport de personnes.
          </p>
          <p style={{ color: C.gold, fontSize: 13, fontWeight: 700, marginBottom: 32 }}>
            ⚡ Prix promo jusqu'au 30 septembre 2026 · Places limitées
          </p>
          <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
            <Link to="/inscription" style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "14px 30px", borderRadius: 10, background: C.gold, color: C.navy, fontWeight: 800, fontSize: 15, textDecoration: "none", boxShadow: "0 8px 24px rgba(201,164,82,0.35)" }}>
              S'inscrire <ArrowRight size={16} />
            </Link>
            <a href="https://wa.me/33749124282" target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "14px 28px", borderRadius: 10, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.2)", color: "#fff", fontWeight: 600, fontSize: 15, textDecoration: "none" }}>
              💬 WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
